import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

const iconVariants = {
  default: 'text-primary-600 bg-primary-50 dark:bg-primary-900/30 dark:text-primary-400',
  success: 'text-success-600 bg-success-50 dark:bg-success-500/20 dark:text-success-500',
  warning: 'text-warning-600 bg-warning-50 dark:bg-warning-500/20 dark:text-warning-500',
  error: 'text-error-600 bg-error-50 dark:bg-error-500/20 dark:text-error-500',
};

const trendColors = {
  default: 'text-gray-500 dark:text-gray-400',
  success: 'text-success-600 dark:text-success-500',
  warning: 'text-warning-600 dark:text-warning-500',
  error: 'text-error-600 dark:text-error-500',
};

export function StatCard({
  title,
  value,
  total,
  icon,
  trend,
  trendUp,
  description,
  variant = 'default',
  clickable = false,
  onClick,
}: {
  title: string;
  value: string | number;
  total?: number;
  icon: React.ReactNode;
  trend?: string;
  trendUp?: boolean;
  description?: string;
  variant?: 'default' | 'success' | 'warning' | 'error';
  clickable?: boolean;
  onClick?: () => void;
}) {
  // The whole-card hover state suggests the entire card is the target, so
  // the click handler lives on `<Card>` (mirrors the alarm-card pattern in
  // `device-stats-cards.tsx`). Previously the handler was on an inner
  // `<button>` inside `<CardContent>`, so taps "outside" the inner button
  // (but still inside the card) didn't navigate. Keyboard activation via
  // Enter / Space is preserved on the card itself.
  const isInteractive = clickable && !!onClick;

  return (
    <Card
      variant="elevated"
      className={cn(
        'transition-shadow',
        isInteractive &&
          'cursor-pointer hover:shadow-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900'
      )}
      onClick={isInteractive ? onClick : undefined}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onKeyDown={
        isInteractive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick!();
              }
            }
          : undefined
      }
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between w-full text-left">
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
            <div className="mt-2 flex items-baseline gap-2">
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
              {total != null && (
                <span className="text-sm text-gray-500 dark:text-gray-400">/ {total}</span>
              )}
            </div>
            {trend && (
              <p className={cn('mt-2 text-sm font-medium', trendUp ? trendColors.success : trendColors[variant])}>
                {trend}
              </p>
            )}
            {description && (
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{description}</p>
            )}
          </div>
          <div className={cn('p-3 rounded-lg flex-shrink-0', iconVariants[variant])}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
